import { useBlocker, type ShouldBlockFn } from '@tanstack/react-router';
import { useCallback, useRef } from 'react';

import { translate } from '../../common/helpers/translate.helper';

interface DemandDefinitionNavigationGuardOptions {
    definitionId?: string;
    hasUnsavedChanges: boolean;
    isBusy: boolean;
}

export const useDemandDefinitionNavigationGuard = ({
    definitionId,
    hasUnsavedChanges,
    isBusy,
}: DemandDefinitionNavigationGuardOptions) => {
    // Set right before navigating away after a save, as formik state is not re-rendered yet at that point
    const isNavigationAllowedRef = useRef(false);

    const shouldBlockWhileBusy = useCallback(() => isBusy && !isNavigationAllowedRef.current, [isBusy]);
    useBlocker({ shouldBlockFn: shouldBlockWhileBusy, enableBeforeUnload: false });

    const shouldConfirmDiscard = useCallback<ShouldBlockFn>(
        ({ next }) => {
            if (isNavigationAllowedRef.current || isBusy || !hasUnsavedChanges) {
                return false;
            }
            const isSameCreateFlow = !definitionId && next.fullPath === '/demand-definitions/create/$substep';
            const isSameEditFlow =
                next.fullPath === '/demand-definitions/edit/$definitionId/$substep' &&
                next.params.definitionId === definitionId;
            if (isSameCreateFlow || isSameEditFlow) {
                return false;
            }
            return !window.confirm(translate('drawer.discardUnsavedChanges'));
        },
        [definitionId, hasUnsavedChanges, isBusy]
    );
    useBlocker({
        shouldBlockFn: shouldConfirmDiscard,
        enableBeforeUnload: hasUnsavedChanges,
    });

    return {
        allowNavigation: () => {
            isNavigationAllowedRef.current = true;
        },
    };
};
